import { useCallback, useEffect, useMemo, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import { formatDate } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import CakeOutlinedIcon from '@mui/icons-material/CakeOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import Header from '../../components/Header';
import ConnString from '../../ConnString.js';
import { tokens } from '../../theme';

const Connstring2 = ConnString.connstring;

const readDateParts = (value) => {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;

  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;

  return { month, day };
};

const toDateKey = (year, month, day) => (
  `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
);

const buildBirthdayEvents = (profiles, range) => {
  if (!range?.start || !range?.end) return [];

  const events = [];
  const firstYear = range.start.getFullYear();
  const lastYear = range.end.getFullYear();

  profiles.forEach((profile) => {
    const dateParts = readDateParts(profile.birthDate);
    if (!dateParts) return;

    for (let year = firstYear; year <= lastYear; year += 1) {
      const occurrence = new Date(year, dateParts.month - 1, dateParts.day);

      // Invalid dates such as February 29 in non-leap years should not roll into March.
      if (occurrence.getMonth() !== dateParts.month - 1 || occurrence.getDate() !== dateParts.day) continue;
      if (occurrence < range.start || occurrence >= range.end) continue;

      events.push({
        id: `client-profile-${profile.compCode}-${year}`,
        title: `Birthday / Anniversary: ${profile.companyName}`,
        date: toDateKey(year, dateParts.month, dateParts.day),
        allDay: true,
        editable: false,
        startEditable: false,
        durationEditable: false,
        backgroundColor: '#ec4899',
        borderColor: '#db2777',
        textColor: '#ffffff',
        extendedProps: {
          eventType: 'clientBirthday',
          compCode: profile.compCode,
          companyName: profile.companyName,
          originalDate: profile.birthDate,
        },
      });
    }
  });

  return events;
};

const Calendar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const selectedCompID = sessionStorage.getItem('compID');
  const [currentEvents, setCurrentEvents] = useState([]);
  const [manualEvents, setManualEvents] = useState([]);
  const [birthdayProfiles, setBirthdayProfiles] = useState([]);
  const [birthdayLoading, setBirthdayLoading] = useState(false);
  const [birthdayError, setBirthdayError] = useState('');
  const [calendarRange, setCalendarRange] = useState(null);

  const fetchClientBirthdays = useCallback(async () => {
    if (!selectedCompID) {
      setBirthdayProfiles([]);
      setBirthdayError('No company is selected.');
      return;
    }

    setBirthdayLoading(true);
    setBirthdayError('');

    try {
      const response = await fetch(`${Connstring2}/api/CalendarEvents`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyID: selectedCompID }),
      });
      const data = await response.json();

      if (!response.ok || !data?.success || !Array.isArray(data.events)) {
        throw new Error(data?.message || 'Unable to load calendar events.');
      }

      const profiles = data.events
        .filter((event) => event.EventType === 'CLIENT_BIRTHDAY')
        .map((event) => ({
          compCode: String(event.CompCode ?? '').trim(),
          companyName: String(event.CompanyName ?? '').trim(),
          birthDate: String(event.EventDate ?? '').trim(),
        }))
        .filter((profile) => profile.compCode && profile.companyName && readDateParts(profile.birthDate));

      setBirthdayProfiles(profiles);
    } catch (error) {
      console.error('Error loading client birthdays for calendar:', error);
      setBirthdayProfiles([]);
      setBirthdayError(error.message || 'Unable to load client birthdays.');
    } finally {
      setBirthdayLoading(false);
    }
  }, [selectedCompID]);

  useEffect(() => {
    fetchClientBirthdays();
  }, [fetchClientBirthdays]);

  const birthdayEvents = useMemo(
    () => buildBirthdayEvents(birthdayProfiles, calendarRange),
    [birthdayProfiles, calendarRange],
  );

  const calendarEvents = useMemo(
    () => [...manualEvents, ...birthdayEvents],
    [manualEvents, birthdayEvents],
  );

  const sidebarEvents = useMemo(
    () => [...currentEvents].sort((left, right) => left.start - right.start),
    [currentEvents],
  );

  const handleDateSelect = (selected) => {
    const title = window.prompt('Please enter a title for the new event.');
    selected.view.calendar.unselect();

    if (!title?.trim()) return;

    setManualEvents((existing) => [
      ...existing,
      {
        id: `manual-${Date.now()}`,
        title: title.trim(),
        start: selected.startStr,
        end: selected.endStr,
        allDay: selected.allDay,
      },
    ]);
  };

  const handleEventClick = (selected) => {
    if (selected.event.extendedProps?.eventType === 'clientBirthday') {
      const { companyName, compCode, originalDate } = selected.event.extendedProps;
      window.alert(`${companyName} (${compCode})\nBirth / incorporation date: ${originalDate}`);
      return;
    }

    if (window.confirm(`Are you sure you want to delete the event '${selected.event.title}'?`)) {
      setManualEvents((existing) => existing.filter((event) => event.id !== selected.event.id));
    }
  };

  const handleDatesSet = useCallback((dateInfo) => {
    setCalendarRange((current) => {
      if (current?.start?.getTime() === dateInfo.start.getTime()
        && current?.end?.getTime() === dateInfo.end.getTime()) {
        return current;
      }

      return { start: dateInfo.start, end: dateInfo.end };
    });
  }, []);

  return (
    <Box m={{ xs: '12px', md: '20px' }}>
      <Header title="Calendar" subtitle="Client birthdays, incorporation anniversaries, and personal events" />

      {birthdayError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {birthdayError}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems="stretch">
        <Paper
          elevation={0}
          sx={{
            flex: { lg: '0 0 290px' },
            p: 2,
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: colors.primary[400],
            maxHeight: { lg: '75vh' },
            overflow: 'auto',
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
            <Typography variant="h5" fontWeight={700}>Events</Typography>
            {birthdayLoading ? (
              <CircularProgress size={20} />
            ) : (
              <Chip
                size="small"
                icon={<CakeOutlinedIcon />}
                label={`${birthdayProfiles.length} profiles`}
                color="secondary"
                variant="outlined"
              />
            )}
          </Stack>

          <Stack direction="row" spacing={2} sx={{ mt: 1.5, color: 'text.secondary' }}>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <CakeOutlinedIcon sx={{ color: '#ec4899', fontSize: 18 }} />
              <Typography variant="caption">Birthday / Anniversary</Typography>
            </Stack>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <EventOutlinedIcon color="primary" sx={{ fontSize: 18 }} />
              <Typography variant="caption">Personal</Typography>
            </Stack>
          </Stack>

          <Divider sx={{ my: 1.5 }} />

          {sidebarEvents.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              No events in the current calendar range.
            </Typography>
          ) : (
            <List disablePadding>
              {sidebarEvents.map((event) => {
                const isBirthday = event.extendedProps?.eventType === 'clientBirthday';
                return (
                  <ListItem
                    key={event.id}
                    sx={{
                      bgcolor: isBirthday ? 'rgba(236,72,153,0.13)' : colors.greenAccent[800],
                      borderLeft: `4px solid ${isBirthday ? '#ec4899' : colors.greenAccent[500]}`,
                      mb: 1,
                      borderRadius: 2,
                    }}
                  >
                    <ListItemText
                      primary={event.title}
                      primaryTypographyProps={{ fontWeight: 600, fontSize: 13 }}
                      secondary={formatDate(event.start, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </Paper>

        <Paper
          elevation={0}
          sx={{
            flex: 1,
            p: { xs: 1, md: 2 },
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
            overflow: 'hidden',
            '& .fc .fc-button-primary': {
              bgcolor: theme.palette.primary.main,
              borderColor: theme.palette.primary.main,
              textTransform: 'capitalize',
            },
            '& .fc .fc-daygrid-day.fc-day-today': {
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(59,130,246,0.16)' : '#eff6ff',
            },
            '& .fc-event': { cursor: 'pointer' },
          }}
        >
          <FullCalendar
            height="75vh"
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay,listMonth',
            }}
            initialView="dayGridMonth"
            editable
            selectable
            selectMirror
            dayMaxEvents
            select={handleDateSelect}
            eventClick={handleEventClick}
            eventChange={(changeInfo) => {
              if (changeInfo.event.extendedProps?.eventType === 'clientBirthday') return;
              setManualEvents((existing) => existing.map((event) => (
                event.id === changeInfo.event.id
                  ? {
                      ...event,
                      start: changeInfo.event.startStr,
                      end: changeInfo.event.endStr || undefined,
                      allDay: changeInfo.event.allDay,
                    }
                  : event
              )));
            }}
            datesSet={handleDatesSet}
            eventsSet={setCurrentEvents}
            events={calendarEvents}
          />
        </Paper>
      </Stack>
    </Box>
  );
};

export default Calendar;
